/**
 * Analytics scaffold — no-op-safe `trackEvent`.
 *
 * Devanza-web doesn't currently ship an App Insights client; the wiring is
 * scheduled in a separate Epic. In the meantime, instrumentation calls land
 * here and become no-ops in production. When the client is wired, this
 * module is the single place to swap in the real implementation — no code
 * changes at the call sites.
 *
 * Design constraints:
 *   - **Must never throw.** A broken analytics path must not break the UI.
 *   - **Must never block.** Calls are fire-and-forget; the caller doesn't
 *     await the result and doesn't get one.
 *   - **Type-safe event names.** Use the `AnalyticsEventName` union below
 *     so we have a typed inventory of every event the app emits.
 *   - **Untyped properties.** Properties vary per event; a strict union
 *     would multiply the inventory by N. Use a loose record and document
 *     the property shape next to each call site.
 *
 * Per AB#1713 / CR-2026-05-17: introducing this scaffold to wire the
 * `staff_modal.bulk_select_clicked` event. Other events follow as we
 * instrument more flows.
 */

export type AnalyticsEventName =
    /**
     * Fired when a Salon Owner clicks "Select all" or "Clear" on a category
     * group inside the Manage Staff add/edit modal's services chip section.
     *
     * Properties:
     *   action: 'select_all' | 'clear'
     *   category_id: number | null     // null for the "Other" group
     *   services_in_group_count: number
     *   total_selected_after_action: number
     */
    | 'staff_modal.bulk_select_clicked'
    /**
     * AB#1796 (CR-2026-05-29) — fired exactly once per successful Quick-Book
     * confirmation (BE returns 200/201). Tracks the phone-capture funnel.
     *
     * No-PII contract: payload MUST contain ONLY these keys; no customer
     * name, customer phone, IP, or user-agent. The unit test in
     * src/components/QuickBookModal/QuickBookModal.analytics.test.ts
     * pins this exact shape.
     *
     * Properties:
     *   businessId: number
     *   branchId: number
     *   serviceId: number
     *   staffId: number
     *   withPhone: boolean    // phone field trimmed length > 0
     *   withName: boolean     // name field trimmed length > 0
     */
    | 'quickBook.completed'
    /**
     * AB#1797 (CR-2026-05-29) — Appointments List V2 events.
     *
     * appointments.list.viewed   — fired once per session on page mount (deduped).
     *   Properties: businessId: number, role: 'owner' | 'staff'
     *
     * appointments.filter.applied — fired on every filter change.
     *   Properties: filterType: string, filterValue: string
     *
     * appointments.markedPaid — fired on successful mark-paid.
     *   No-PII: only IDs + method. No customer name/phone.
     *   Properties: appointmentId: number, paymentMethod: string, source: 'list' | 'calendar'
     *
     * appointments.signalrPaymentEventReceived — debug only, non-prod.
     *   Properties: appointmentId: number, paymentStatus: string, source: string
     */
    | 'appointments.list.viewed'
    | 'appointments.filter.applied'
    | 'appointments.markedPaid'
    | 'appointments.signalrPaymentEventReceived'
    /**
     * AB#1904 (W-1) — Stripe Checkout upgrade flow.
     *
     * `subscription.upgradeIntent`         — Owner clicks Upgrade on a plan card.
     *   Properties: fromPlan, toPlan, attributionFrom, attributionFeature
     * `subscription.upgradeConfirmed`      — UpgradeConfirmModal Continue clicked.
     *   Properties: fromPlan, toPlan, billingCycle
     * `subscription.checkoutSessionCreated` — Mint-session 200.
     *   Properties: fromPlan, toPlan
     * `subscription.checkoutSessionFailed`  — Mint-session error.
     *   Properties: fromPlan, toPlan, errorCode
     * `subscription.upgradeCompleted`       — Success page reached active state.
     *   Properties: fromPlan, toPlan, pollAttempts
     * `subscription.upgradeProcessing`      — Success page entered polling state.
     *   Properties: toPlan
     * `subscription.upgradePollingTimeout`  — Success page exhausted polls.
     *   Properties: toPlan, sessionId
     * `subscription.upgradeVerificationFailed` — Success page error fork.
     *   Properties: sessionId, errorCode
     * `subscription.upgradeCancelled`        — `/subscription/cancelled` mounted.
     *   Properties: fromPlan, intendedPlan
     * `subscription.cancelReason`            — Cancel-survey chip clicked.
     *   Properties: reason
     * `subscription.postUpgradeBannerShown`        — Dashboard banner mounted.
     * `subscription.postUpgradeBannerCtaClicked`   — Banner CTA clicked.
     * `subscription.postUpgradeBannerDismissed`    — Banner × clicked.
     *   Properties: toPlan
     * `subscription.enterpriseLeadSubmitted`       — Enterprise modal submit.
     *   Properties: estimatedBranches, estimatedStaff, hasMessage
     * `subscription.tierCapHit`                    — Cap blocker viewed.
     *   Properties: capType, currentPlan
     *
     * PDPL: never include userId or email; tenantId only when needed.
     */
    | 'subscription.upgradeIntent'
    | 'subscription.upgradeConfirmed'
    | 'subscription.checkoutSessionCreated'
    | 'subscription.checkoutSessionFailed'
    | 'subscription.upgradeCompleted'
    | 'subscription.upgradeProcessing'
    | 'subscription.upgradePollingTimeout'
    | 'subscription.upgradeVerificationFailed'
    | 'subscription.upgradeCancelled'
    | 'subscription.cancelReason'
    | 'subscription.postUpgradeBannerShown'
    | 'subscription.postUpgradeBannerCtaClicked'
    | 'subscription.postUpgradeBannerDismissed'
    | 'subscription.enterpriseLeadSubmitted'
    | 'subscription.tierCapHit';

/**
 * AB#1796 — Allow-list of property keys for `quickBook.completed`. Kept as
 * a runtime constant so the unit test can pin the no-PII guarantee without
 * mirroring the type definition by hand. If you add a property to the
 * event, add it here AND to the union doc above.
 */
export const QUICK_BOOK_COMPLETED_PROPERTY_KEYS = [
    'businessId',
    'branchId',
    'serviceId',
    'staffId',
    'withPhone',
    'withName',
] as const;

export type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

/**
 * Fire an analytics event. No-op-safe; never throws; never blocks the UI.
 *
 * Until the App Insights client is wired, this logs to the console under
 * a `[analytics]` prefix when `NODE_ENV !== 'production'` so the dev team
 * can see events fire during local testing. The production build silently
 * drops them.
 */
export function trackEvent(name: AnalyticsEventName, properties?: AnalyticsProperties): void {
    try {
        // Hook point — replace with App Insights `trackEvent({ name, properties })`
        // when the client lands. Keep the same signature so call sites don't
        // change. See AB-TBD (App Insights wiring Epic).
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[analytics]', name, properties ?? {});
        }
    } catch {
        // Swallow — broken analytics must not break the UI.
    }
}
